import {
  type InvestigationActivity as RxInvestigationActivity,
  useRxCollectionInvestigationActivity,
} from "$global-providers/rxdb-provider/collections/investigation-activity";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useUser } from "$global-providers/user-provider";
import { usePaymentStatus } from "$routes/billing/_lib/pre-service/use-payment-status";
import { PriceableItemOrigin, Role } from "elephant-web/gql";
import { useCallback } from "react";
import type { WithDeleted } from "rxdb";
import { L10N, translateI18nObject } from "../../_lib/translation-util";
import { InvestigationActivityForm } from "./investigation-activity-form";
import { InvestigationActivityResults } from "./investigation-activity-results";
import { InvestigationCard } from "./investigation-card";
import { populateOrderedSchema } from "./populate-ordered-schema";
import type { TranslatedInvestigationCatalogueItem } from "./util";

const ROLES_FOR_FORM = [Role.Nurse, Role.Doctor, Role.Technician];

export const InvestigationActivityOutcome: FC<{
  investigationActivity: RxInvestigationActivity;
}> = ({ investigationActivity }) => {
  const { role, id: userId, firstName, lastName } = useUser();
  const { getCurrentTime } = useServerTime();
  const investigationActivityCollection =
    useRxCollectionInvestigationActivity();

  const isCreator = investigationActivity.meta.createdByUserId === userId;
  const canCompleteForm =
    !investigationActivity.outcome &&
    (ROLES_FOR_FORM.includes(role) || isCreator);

  const paymentStatus = usePaymentStatus({
    patientId: investigationActivity.patientId,
    externalReference: investigationActivity.catalogueItem?.id
      ? {
          origin: PriceableItemOrigin.Investigation,
          foreignId: investigationActivity.catalogueItem.id,
        }
      : null,
  });

  const completeInvestigation = useCallback(
    async ({
      investigationRequest,
      investigationId,
      formResults,
    }: {
      investigationRequest: RxInvestigationActivity["request"];
      investigationId: RxInvestigationActivity["id"];
      formResults: { [prop: string]: any };
    }) => {
      if (!investigationRequest || !investigationId) return null;

      const createdOutcomeAt = getCurrentTime().toISOString();

      const populatedOutcomeSchema = populateOrderedSchema(
        formResults,
        investigationActivity.catalogueItem.result.resultSchema,
      );
      const outcome: RxInvestigationActivity["outcome"] = {
        ...investigationRequest,
        orderedProperties: populatedOutcomeSchema.orderedProperties,
        performer: {
          id: userId,
          name: `${firstName} ${lastName}`,
        },
        meta: {
          updated: createdOutcomeAt,
          created: createdOutcomeAt,
        },
      };

      const merged: WithDeleted<RxInvestigationActivity> = {
        ...investigationActivity,
        outcome,
        request: {
          ...investigationRequest,
          meta: {
            ...investigationRequest.meta,
            updated: createdOutcomeAt,
          },
        },
        meta: {
          ...investigationActivity.meta,
          updatedAt: createdOutcomeAt,
          updatedByUserId: userId,
        },
        _deleted: false,
      };
      await investigationActivityCollection.upsert(merged);
    },
    [
      investigationActivity,
      investigationActivityCollection,
      getCurrentTime,
      userId,
      firstName,
      lastName,
    ],
  );
  return (
    <InvestigationCard
      catalogueItem={
        translateI18nObject(
          investigationActivity.catalogueItem,
          L10N.EN,
        ) as unknown as TranslatedInvestigationCatalogueItem
      }
      outcome={investigationActivity.outcome}
      investigationId={investigationActivity.id}
      request={investigationActivity.request}
      isValid={true}
      canEdit={canCompleteForm}
      paymentStatus={paymentStatus}
    >
      {canCompleteForm ? (
        <InvestigationActivityForm
          investigationId={investigationActivity.id}
          investigationRequest={investigationActivity.request}
          completeInvestigation={completeInvestigation}
          disabled={false}
          paymentBlocked={paymentStatus === "awaiting"}
        />
      ) : (
        <InvestigationActivityResults
          property={
            investigationActivity.outcome ?? investigationActivity.request
          }
        />
      )}
    </InvestigationCard>
  );
};
